import { DeleteOutlined, MailOutlined, PhoneOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, List, Popconfirm } from "antd";
import { useState } from "react";
import { useMutation } from "urql";
import CreateContactPersonModal from "./CreateContactPersonModal";
import UpdateContactPersonModal from "./UpdateContactPersonModal";

import { graphql } from "@/__graphql__";

interface ContactPersonSummary {
  id: string;
  contactPerson: {
    id: string;
    name: string;
    emailAddresses: Array<string> | null;
    phoneNumbers: Array<string> | null;
  };
}

interface ContactPersonsSectionProps {
  contactPersons: ContactPersonSummary[];
  locationId: string;
  relationId: string;
}

export function ContactPersonsSection({ contactPersons, locationId, relationId }: ContactPersonsSectionProps) {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editingContactPerson, setEditingContactPerson] = useState<ContactPersonSummary | null>(null);

  const [, removeContactPerson] = useMutation(RemoveContactPersonFromLocationMutation);

  return (
    <>
      <Button icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)} size="small">
        Contactpersoon toevoegen
      </Button>
      <List
        dataSource={contactPersons}
        itemLayout="horizontal"
        renderItem={(item) => (
          <List.Item
            actions={[
              <Popconfirm
                key="delete"
                title="Weet je zeker dat je deze contactpersoon bij de locatie wil verwijderen?"
                description="Let op: deze contactpersoon kan nog steeds gekoppeld zijn aan andere locaties."
                onConfirm={(e?: React.MouseEvent) => {
                  e?.stopPropagation();
                  removeContactPerson({
                    input: { relationId, locationId, contactPersonId: item.contactPerson.id },
                  });
                }}
              >
                <Button danger icon={<DeleteOutlined />} size="small" type="text" onClick={(e) => e.stopPropagation()} />
              </Popconfirm>,
            ]}
            style={{ cursor: "pointer" }}
            onClick={() => setEditingContactPerson(item)}
          >
            <List.Item.Meta
              title={item.contactPerson.name}
              description={
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {item.contactPerson.emailAddresses && item.contactPerson.emailAddresses.length > 0 && (
                    <span>
                      <MailOutlined style={{ marginRight: 6 }} />
                      {item.contactPerson.emailAddresses.join(", ")}
                    </span>
                  )}
                  {item.contactPerson.phoneNumbers && item.contactPerson.phoneNumbers.length > 0 && (
                    <span>
                      <PhoneOutlined style={{ marginRight: 6 }} />
                      {item.contactPerson.phoneNumbers.join(", ")}
                    </span>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />

      <UpdateContactPersonModal
        open={editingContactPerson !== null}
        onClose={() => setEditingContactPerson(null)}
        initialValues={{
          contactPersonId: editingContactPerson?.contactPerson.id ?? "",
          name: editingContactPerson?.contactPerson.name ?? "",
          emailAddresses: editingContactPerson?.contactPerson.emailAddresses,
          phoneNumbers: editingContactPerson?.contactPerson.phoneNumbers,
        }}
      />
      <CreateContactPersonModal
        open={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        locationId={locationId}
        relationId={relationId}
      />
    </>
  );
}

const RemoveContactPersonFromLocationMutation = graphql(`
  mutation Dashboard__ContactPersonsSection__RemoveContactPersonFromLocationMutation($input: RemoveContactPersonFromLocationInput!) {
    removeContactPersonFromLocation(input: $input) {
      location {
        id
        contactPersons {
          id
          contactPerson {
            id
            name
            emailAddresses
            phoneNumbers
          }
        }
      }
    }
  }
`);
